import { type MappedAssetSensor } from '@/common/asset-sensor';

const PIN_PATH = new Path2D(
	'M12.5 0C5.596 0 0 5.596 0 12.5c0 9.375 12.5 28.125 12.5 28.125S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z'
);

const STYLES = {
	font: 'bold 13px sans-serif',
	paddingX: 8,
	paddingY: 5,
	borderRadius: 6,
	borderWidth: 1.5,
	pinHeight: 41,
	pinWidth: 25,
	gap: 2, // Space between pin and bubble
	shadowBlur: 6,
};

/**
 * Generates a Data URI containing both the Label Bubble and the Pin
 */
export const createCombinedSensorImage = (
	sensor: MappedAssetSensor,
	isSelected: boolean,
	isDarkMode: boolean = false
): string => {
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	if (!ctx) return '';

	// Theme-based colors
	const theme = {
		bg: isDarkMode ? '#18181b' : '#ffffff', // Card background
		text: isDarkMode ? '#fafafa' : '#000000', // Foreground text
		shadow: isDarkMode ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.25)', // Stronger shadow for dark mode
		defaultPinStroke: isDarkMode ? '#52525b' : '#000000', // Border around pin
	};

	// 1. Extract Colors and Text
	const labelText = sensor.label;
	const statusText = `(${sensor.statusSettings.label})`;
	const statusColor = sensor.statusSettings.map.statusColor; // e.g. Green
	const borderColor = sensor.statusSettings.map.borderColor; // Border of bubble
	const pinColor = isSelected ? '#2563EB' : sensor.statusSettings.map.iconColor;
	const pinStroke = isSelected ? '#1E40AF' : theme.defaultPinStroke;

	// 2. Measure Text to determine Bubble Size
	ctx.font = STYLES.font;
	const labelWidth = ctx.measureText(labelText).width;
	const statusWidth = ctx.measureText(statusText).width;
	const spaceWidth = 4;

	const bubbleWidth = labelWidth + spaceWidth + statusWidth + STYLES.paddingX * 2;
	const bubbleHeight = 16 + STYLES.paddingY * 2; // approx text height + padding

	// 3. Resize Canvas
	// Width: Max of bubble or pin, plus shadow buffer
	// Height: Bubble + Gap + Pin + Shadow buffer
	const totalWidth = Math.max(bubbleWidth, STYLES.pinWidth) + 20;
	const totalHeight = bubbleHeight + STYLES.gap + STYLES.pinHeight + 20;

	canvas.width = totalWidth;
	canvas.height = totalHeight;

	// Reset context after resize (some browsers clear it)
	ctx.font = STYLES.font;

	// Calculate Centers
	const centerX = totalWidth / 2;
	const bubbleY = 10; // Start slightly down to allow for top shadow

	// ------------------------------------------
	// DRAW: Drop Shadow (Applied to everything drawn next)
	// ------------------------------------------
	ctx.shadowColor = theme.shadow;
	ctx.shadowBlur = STYLES.shadowBlur;
	ctx.shadowOffsetX = 0;
	ctx.shadowOffsetY = 3;

	// ------------------------------------------
	// DRAW: Bubble (Rounded Rect)
	// ------------------------------------------
	const bubbleX = centerX - bubbleWidth / 2;

	ctx.beginPath();
	ctx.roundRect(bubbleX, bubbleY, bubbleWidth, bubbleHeight, STYLES.borderRadius);
	ctx.fillStyle = theme.bg;
	ctx.fill();

	// Bubble Border
	ctx.lineWidth = STYLES.borderWidth;
	ctx.strokeStyle = borderColor;
	ctx.stroke();

	// ------------------------------------------
	// DRAW: Pin
	// ------------------------------------------
	// We save context to translate to the pin position
	ctx.save();
	const pinX = centerX - STYLES.pinWidth / 2;
	const pinY = bubbleY + bubbleHeight + STYLES.gap;

	ctx.translate(pinX, pinY);

	// Draw Pin Shape
	ctx.fillStyle = pinColor;
	ctx.strokeStyle = pinStroke;
	ctx.lineWidth = isSelected ? 2 : 1;

	ctx.fill(PIN_PATH);
	ctx.stroke(PIN_PATH);

	// Draw White Dot inside Pin
	ctx.beginPath();
	ctx.arc(12.5, 12.5, 6, 0, Math.PI * 2);
	ctx.fillStyle = '#ffffff'; // Dot stays white regardless of theme
	ctx.fill();

	ctx.restore();

	// ------------------------------------------
	// DRAW: Text (Reset Shadow so text is crisp)
	// ------------------------------------------
	ctx.shadowColor = 'transparent';

	const textY = bubbleY + STYLES.paddingY + 12; // 12 is approx cap height

	// Draw "TPS_452" (Dynamic Foreground)
	ctx.fillStyle = theme.text;
	ctx.textAlign = 'left';
	ctx.fillText(labelText, bubbleX + STYLES.paddingX, textY);

	// Draw "(OK)" (Status Color)
	ctx.fillStyle = statusColor; // Use the dynamic status color
	ctx.font = 'bold 13px sans-serif'; // Ensure bold
	ctx.fillText(statusText, bubbleX + STYLES.paddingX + labelWidth + spaceWidth, textY);

	return canvas.toDataURL();
};
